/**
 * MoreActionsMenu — overflow dropdown primitive for actions + tabs.
 *
 * Push 4-1o (2026-07-09). Same dropdown vibe as the Capabilities top-level
 * nav dropdown (TopNav.tsx#CapabilitiesDropdown) — pill button labeled
 * "More" with a chevron, opens a portal-positioned menu below the trigger.
 * Two primary consumers:
 *
 *   1. Highlight-bar action overflow — when a record has >N primary
 *      actions, keep the first N as visible buttons and stuff the rest
 *      behind "More ▾" so the highlight bar never wraps or crowds.
 *   2. LightningTabs tab overflow — same pattern applied to sub-tabs on
 *      admin detail pages. First N tabs render as normal tab buttons;
 *      overflow lands under a "More ▾" tail.
 *
 * The primitive itself is stateless w.r.t. the items — caller passes
 * `items`, each with `label` + `onClick` (or `to` for React Router link
 * items) + optional `danger`/`disabled`/`hint`. Menu closes on item
 * click, click-outside, or Escape.
 *
 * Composition note: no button-cluster geometry inside the menu. Each
 * item is a full-width row with hover-wash. The trigger button MUST be
 * rendered inside a ButtonCluster (or a manual cluster row) so its
 * height matches sibling actions — MoreActionsMenu doesn't own the
 * cluster geometry.
 */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

/** A single overflow menu item. Exactly one of onClick / to must be set. */
export interface MoreMenuItem {
  /** Stable key. Used for React reconciliation. */
  key: string;
  /** Visible label. */
  label: string;
  /** Click handler. Provide EITHER onClick OR to, not both. */
  onClick?: () => void;
  /** React Router route. Provide EITHER onClick OR to, not both. */
  to?: string;
  /** When true, renders in danger-red for destructive actions. */
  danger?: boolean;
  /** When true, item is visually subdued and not interactive. */
  disabled?: boolean;
  /** Optional secondary line rendered below the label at muted color. */
  hint?: string;
  /** Optional leading icon (16px). */
  icon?: ReactNode;
}

interface MoreActionsMenuProps {
  /** Overflow items. When empty, the menu still renders its trigger but
   *  opening it shows an empty state — caller usually gates rendering. */
  items: MoreMenuItem[];
  /** Trigger label. Defaults to "More" — override for tab-overflow
   *  (`triggerLabel="More tabs"`) or context-specific framing. */
  triggerLabel?: string;
  /** Minimum menu width in pixels. Defaults to 200px. */
  minWidth?: number;
  /** Match the sibling button's variant so the trigger looks native in
   *  the surrounding cluster. Defaults to 'secondary'. */
  variant?: 'primary' | 'secondary' | 'ghost';
}

const MORE_MENU_MIN_WIDTH = 200;
const MORE_MENU_GAP = 6;

interface MenuRect {
  top: number;
  left: number;
}

export function MoreActionsMenu({
  items,
  triggerLabel = 'More',
  minWidth = MORE_MENU_MIN_WIDTH,
  variant = 'secondary',
}: MoreActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<MenuRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  function computeRect(): MenuRect | null {
    const btn = triggerRef.current;
    if (btn === null) return null;
    const r = btn.getBoundingClientRect();
    // Right-align to trigger (highlight-bar actions cluster is right-side).
    // Keep at least 8px from viewport edges.
    let left = r.right - minWidth;
    if (left + minWidth > window.innerWidth - 8) {
      left = window.innerWidth - minWidth - 8;
    }
    if (left < 8) left = 8;
    return { top: r.bottom + MORE_MENU_GAP, left };
  }

  useLayoutEffect(() => {
    if (!open) return;
    setRect(computeRect());
    function onResize() {
      setRect(computeRect());
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, minWidth]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (triggerRef.current?.contains(t) === true) return;
      if (menuRef.current?.contains(t) === true) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function handleItemClick(item: MoreMenuItem, e: ReactMouseEvent) {
    if (item.disabled === true) {
      e.preventDefault();
      return;
    }
    setOpen(false);
    if (item.onClick !== undefined) {
      e.preventDefault();
      item.onClick();
    }
    // If item.to is set, the Link handles navigation on its own.
  }

  const buttonClass =
    variant === 'primary'
      ? 'btn btn-primary'
      : variant === 'ghost'
        ? 'btn btn-ghost'
        : 'btn btn-secondary';

  return (
    <>
      {/* Push 4-1t (2026-07-09): icon-only chevron trigger — dropped the
         "More" text label per Ean nitpick ("more text feels redundant if
         an icon within a smaller button does it"). Aria-label preserves
         a11y; visual is a compact chevron button matching sibling record-
         detail actions. */}
      <button
        ref={triggerRef}
        type="button"
        className={`${buttonClass} more-actions-trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={triggerLabel === 'More' ? 'More actions' : triggerLabel}
        title={triggerLabel === 'More' ? 'More actions' : triggerLabel}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 16 16"
          fill="none"
          aria-hidden="true"
          style={{
            transition: 'transform 120ms ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && rect !== null &&
        createPortal(
          <div
            ref={menuRef}
            className="more-actions-menu"
            role="menu"
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              minWidth,
            }}
          >
            {items.length === 0 ? (
              <div className="more-actions-empty">Nothing here yet</div>
            ) : (
              items.map((item) =>
                item.to !== undefined ? (
                  <Link
                    key={item.key}
                    to={item.to}
                    role="menuitem"
                    className={`more-actions-item${item.danger === true ? ' danger' : ''}${item.disabled === true ? ' disabled' : ''}`}
                    onClick={(e) => handleItemClick(item, e)}
                  >
                    {item.icon !== undefined && (
                      <span className="more-actions-item-icon" aria-hidden="true">
                        {item.icon}
                      </span>
                    )}
                    <span className="more-actions-item-body">
                      <span className="more-actions-item-label">{item.label}</span>
                      {item.hint !== undefined && (
                        <span className="more-actions-item-hint">{item.hint}</span>
                      )}
                    </span>
                  </Link>
                ) : (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    className={`more-actions-item${item.danger === true ? ' danger' : ''}${item.disabled === true ? ' disabled' : ''}`}
                    onClick={(e) => handleItemClick(item, e)}
                    disabled={item.disabled}
                  >
                    {item.icon !== undefined && (
                      <span className="more-actions-item-icon" aria-hidden="true">
                        {item.icon}
                      </span>
                    )}
                    <span className="more-actions-item-body">
                      <span className="more-actions-item-label">{item.label}</span>
                      {item.hint !== undefined && (
                        <span className="more-actions-item-hint">{item.hint}</span>
                      )}
                    </span>
                  </button>
                ),
              )
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
